/**
 * Pure model for the ASWB/Prometric-style "highlighter" study aid: a user
 * selects text in the question stem and marks it, exactly like the real exam
 * software. No React, no DOM, no storage here — just character-offset range
 * math, so it's cheap to test exhaustively and safe to reuse from any
 * renderer (web today, iOS's SimulationView/QuizPlayerView later).
 *
 * Ranges are half-open [start, end) character offsets into the raw question
 * text, matching the semantics of String.prototype.slice.
 */

export interface HighlightRange {
  start: number;
  end: number;
}

/**
 * Which of the two ASWB/Prometric marker gestures a range came from. The math
 * below is kind-agnostic: each kind owns its own list of ranges.
 */
export type MarkKind = 'highlight' | 'strike';

/** Sort by start, then fold any overlapping or touching ranges into one. */
export function mergeRanges(ranges: HighlightRange[]): HighlightRange[] {
  if (ranges.length === 0) return [];

  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: HighlightRange[] = [{ ...sorted[0] }];

  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const current = sorted[i];
    if (current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push({ ...current });
    }
  }

  return merged;
}

/** Remove `remove` from `ranges`, splitting a range in two if the cut falls in its middle. */
export function subtractRange(ranges: HighlightRange[], remove: HighlightRange): HighlightRange[] {
  const result: HighlightRange[] = [];

  for (const range of ranges) {
    const noOverlap = remove.end <= range.start || remove.start >= range.end;
    if (noOverlap) {
      result.push(range);
      continue;
    }
    if (remove.start > range.start) {
      result.push({ start: range.start, end: Math.min(remove.start, range.end) });
    }
    if (remove.end < range.end) {
      result.push({ start: Math.max(remove.end, range.start), end: range.end });
    }
  }

  return result;
}

/** Does `selection` share at least one character with any range in `ranges`? */
export function overlapsAny(ranges: HighlightRange[], selection: HighlightRange): boolean {
  return ranges.some((range) => selection.start < range.end && selection.end > range.start);
}

/**
 * The single interaction a user has: select some text, click "Highlight".
 * - The selection sits entirely inside one existing highlight -> that's the
 *   user re-selecting what they already marked to take it back off (subtract).
 * - Anything else (no overlap, or a selection that only partially overlaps
 *   and reaches into new territory) -> add/extend the highlight. A drag that
 *   starts on marked text and continues onto unmarked text is read as
 *   "highlight all of this", not "punch a hole in the middle" — that matches
 *   how real highlighter tools (and the physical ASWB/Prometric marker) behave.
 * - A collapsed selection (start === end, or inverted) is a no-op.
 */
export function toggleRange(ranges: HighlightRange[], selection: HighlightRange): HighlightRange[] {
  if (selection.start >= selection.end) return ranges;

  const isFullyContained = ranges.some(
    (range) => selection.start >= range.start && selection.end <= range.end,
  );
  if (isFullyContained) {
    return subtractRange(ranges, selection);
  }

  return mergeRanges([...ranges, selection]);
}

const WORD_BOUNDARY = /[^\s]/;

/**
 * Expand an arbitrary (possibly mid-word) character selection out to the
 * nearest whole-word boundaries. Mouse/touch drag-selects rarely land
 * exactly on a word edge; snapping keeps highlights readable (whole words,
 * never a chopped-off "cli" from "client") and keeps the range model in the
 * same word-granularity as the narration's word-token highlighting.
 */
export function snapToWordBoundaries(text: string, start: number, end: number): HighlightRange {
  let snappedStart = start;
  while (snappedStart > 0 && WORD_BOUNDARY.test(text[snappedStart - 1] ?? '')) {
    snappedStart -= 1;
  }

  let snappedEnd = end;
  while (snappedEnd < text.length && WORD_BOUNDARY.test(text[snappedEnd] ?? '')) {
    snappedEnd += 1;
  }

  return { start: snappedStart, end: snappedEnd };
}
